import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import ChartHeader from 'components/charts/ChartHeader';
import CompositeFilter, { ANALYSIS_FILTERS, getActiveFilters } from 'components/charts/CompositeFilter';
import { DataCard } from 'components/charts/DataCards';
import BarChartV2 from 'components/charts/chartJS/BarChart';
import Button from 'components/field/Button';
import Card from 'components/global/Card';
import { FilterAction } from 'components/list/DynamicListFilterUtils';

import useBrandManagerFilter from 'hooks/useBrandManagerFilter';
import useFetchAndParseOnView from 'hooks/useFetchAndParseOnView';

import { ThreeCardsInRow } from 'pages/Dashboard/Dashboard.styled';

import { REPORT } from 'services/consts';

import { colorCombinations } from 'utils/colors';
import PATHS, { mergePaths } from 'utils/paths';

import { faTruckClock } from '@fortawesome/pro-regular-svg-icons';

import * as S from './OverviewCharts.styled';

const filtersList = [
	ANALYSIS_FILTERS.shipmentCreatedDate,
	ANALYSIS_FILTERS.originCountry,
	ANALYSIS_FILTERS.originCity,
	ANALYSIS_FILTERS.brand,
	ANALYSIS_FILTERS.destinationCountry,
	ANALYSIS_FILTERS.destinationCity,
	ANALYSIS_FILTERS.deliveryCompanyName,
	ANALYSIS_FILTERS.shipmentFlow,
	ANALYSIS_FILTERS.serviceType
];

export const OverviewCharts = ({ widgetName, onApplyFilters, defaultFilters, isBrandManager, assignedBrands }) => {
	const [filtersMenuButtonRef, setFilterMenuButtonRef] = useState(null);
	const [extraFilters, setExtraFilters] = useState(filtersList);
	const navigate = useNavigate();

	useBrandManagerFilter({ isBrandManager, assignedBrands, setExtraFilters });

	const body = useMemo(
		() => ({
			filter: getActiveFilters(extraFilters)
		}),
		[extraFilters]
	);

	const skipNextApply = useRef(false);

	useEffect(() => {
		if (defaultFilters) {
			if (Array.isArray(defaultFilters) && defaultFilters.length > 0) {
				setExtraFilters((prevFilters) =>
					prevFilters.map((filterItem) => {
						const saved = defaultFilters.find((f) => {
							return f.filterValues.key[0] === filterItem.filterValues.key[0];
						});
						return saved ? { ...filterItem, filterValues: saved.filterValues } : filterItem;
					})
				);
			}
			skipNextApply.current = true;
		}
	}, [defaultFilters]);

	useEffect(() => {
		if (onApplyFilters) {
			if (skipNextApply.current) {
				skipNextApply.current = false;
				return;
			}
			onApplyFilters(extraFilters);
		}
	}, [extraFilters]);

	const { ref, data, loading } = useFetchAndParseOnView({
		url: REPORT.DELIVERY_COMPANY.GET_AGGREGATOR_AGING_ANALYSIS_CLIENT,
		body
	});

	const handleNumberCard = (key) => {
		const filter = {
			accessor: 'delayType',
			action: FilterAction.is,
			value: []
		};
		switch (key) {
			case 'pickupAging':
				filter.value = ['pickupDelay', 'reversePickupDelay'];
				break;
			case 'deliveryAging':
				filter.value = ['deliveryDelay', 'reverseDeliveryDelay'];
				break;
			case 'totalAging':
				filter.value = ['pickupDelay', 'reversePickupDelay', 'deliveryDelay', 'reverseDeliveryDelay'];
				break;
			default:
				break;
		}
		navigate(mergePaths(PATHS.ANALYSIS, PATHS.ANALYSIS_TAB), {
			state: {
				cardFilter: filter,
				analysisFilters: extraFilters
					.map((f) => f.filterValues)
					.filter((f) => f.key !== 'delayType' && ((f.value && f.value.length > 0) || (f.from && f.to)))
					.map((f) => ({
						accessor: f.key[0] === 'deliveryCompanyName' ? 'deliveryCompanySettingsName' : f.key[0],
						action: f.action,
						value: f.from && f.to ? { start: f.from, end: f.to } : f.value
					}))
			}
		});
	};

	const NUMBERS_CARDS_CONFIG = [
		{
			accessor: 'totalPickupCount',
			label: 'pendingPickup',
			customIcon: 'fa-regular-box-clock',
			backgroundColor: '#FF950014',
			color: '#FF9500',
			onButtonClick: () => handleNumberCard('pickupAging')
		},
		{
			accessor: 'totalDeliveryCount',
			label: 'pendingDelivery',
			icon: faTruckClock,
			backgroundColor: '#AB47BC14',
			color: '#AB47BC',
			onButtonClick: () => handleNumberCard('deliveryAging')
		},
		{
			accessor: 'totalCount',
			label: 'totalPending',
			customIcon: 'fa-solid fa-truck-fast',
			backgroundColor: '#256CF014',
			color: '#256CF0',
			onButtonClick: () => handleNumberCard('totalAging')
		}
	];

	return (
		<S.Container ref={ref}>
			<div>
				<ChartHeader
					title={widgetName || 'overview'}
					subtitle={'analysis.overviewSection.subtitle'}
					showDivider={false}
					filter={
						<>
							<Button
								id="filters-button"
								lordIcon="https://storage.googleapis.com/tryoto-public/lordicon-compressed/outline/filter-icon.json.gz"
								ref={setFilterMenuButtonRef}
								variant="borderless"
								label="filters"
								className="py-0"
							/>
							<CompositeFilter
								filtersMenuButtonRef={filtersMenuButtonRef}
								filtersList={extraFilters}
								setFiltersList={setExtraFilters}
							/>
						</>
					}
				/>
				<ThreeCardsInRow>
					{NUMBERS_CARDS_CONFIG.map((config) => (
						<DataCard
							key={config.accessor}
							loading={loading}
							cardConfig={config}
							data={data?.totalNumbers?.[config.accessor] ?? '-'}
						/>
					))}
				</ThreeCardsInRow>
			</div>
		</S.Container>
	);
};

const prepareChartData = ({ data, prefix = 'count', t }) => {
	const sortByCount = (itemList, prefix) => {
		const sortedItems = itemList.sort((a, b) => parseInt(a.replace(prefix, '')) - parseInt(b.replace(prefix, '')));

		const dayItems = sortedItems.filter((item) => item.includes('Day'));
		const weekItems = sortedItems.filter((item) => item.includes('Week'));

		return dayItems.concat(weekItems);
	};

	const notTranslatedLabels = sortByCount(Object.keys(data ?? {}), prefix);
	try {
		const labels = notTranslatedLabels.map((item) => t(item));
		const datasets = [
			{
				data: notTranslatedLabels.map((item) => data[item]),
				backgroundColor: notTranslatedLabels.map(
					(_, index) =>
						colorCombinations[
							index < 2
								? colorCombinations.length - 1
								: index < 8
									? colorCombinations.length - 2
									: colorCombinations.length - 3
						].color
				),
				borderColor: notTranslatedLabels.map(
					(_, index) =>
						colorCombinations[
							index < 2
								? colorCombinations.length - 1
								: index < 8
									? colorCombinations.length - 2
									: colorCombinations.length - 3
						].color
				)
			}
		];
		return { labels, datasets };
	} catch {
		return {
			labels: [],
			datasets: [{ data: [0] }]
		};
	}
};

export const PickupAging = ({ widgetName, onApplyFilters, defaultFilters, isBrandManager, assignedBrands }) => {
	const { t } = useTranslation();

	const [filtersMenuButtonRef, setFilterMenuButtonRef] = useState(null);
	const [extraFilters, setExtraFilters] = useState(filtersList);

	useBrandManagerFilter({ isBrandManager, assignedBrands, setExtraFilters });

	const body = useMemo(
		() => ({
			filter: getActiveFilters(extraFilters)
		}),
		[extraFilters]
	);

	const skipNextApply = useRef(false);

	useEffect(() => {
		if (defaultFilters) {
			if (Array.isArray(defaultFilters.filter) && defaultFilters.filter.length > 0) {
				setExtraFilters((prevFilters) =>
					prevFilters.map((filterItem) => {
						const saved = defaultFilters.filter.find((f) => {
							return f.key[0] === filterItem.filterValues.key[0];
						});
						return saved ? { ...filterItem, filterValues: saved } : filterItem;
					})
				);
			}
			skipNextApply.current = true;
		}
	}, [defaultFilters]);

	useEffect(() => {
		if (onApplyFilters) {
			if (skipNextApply.current) {
				skipNextApply.current = false;
				return;
			}
			onApplyFilters(body);
		}
	}, [body]);

	const { ref, data, loading } = useFetchAndParseOnView({
		url: REPORT.DELIVERY_COMPANY.GET_PICKUP_AGING_LIST_CLIENT,
		body,
		parseData: (data) => {
			return prepareChartData({ data: data?.pickupCounts, t });
		}
	});

	return (
		<Card ref={ref}>
			<ChartHeader
				title={widgetName || 'pickupAging'}
				filter={
					<>
						<Button
							id="filters-button"
							lordIcon="https://storage.googleapis.com/tryoto-public/lordicon-compressed/outline/filter-icon.json.gz"
							ref={setFilterMenuButtonRef}
							variant="borderless"
							label="filters"
							className="py-0"
						/>
						<CompositeFilter
							filtersMenuButtonRef={filtersMenuButtonRef}
							filtersList={extraFilters}
							setFiltersList={setExtraFilters}
						/>
					</>
				}
			/>
			<BarChartV2 data={data} loading={loading} />
		</Card>
	);
};

export const DeliveryAging = ({ widgetName, onApplyFilters, defaultFilters, isBrandManager, assignedBrands }) => {
	const { t } = useTranslation();

	const [filtersMenuButtonRef, setFilterMenuButtonRef] = useState(null);
	const [extraFilters, setExtraFilters] = useState(filtersList);

	useBrandManagerFilter({ isBrandManager, assignedBrands, setExtraFilters });

	const body = useMemo(
		() => ({
			filter: getActiveFilters(extraFilters)
		}),
		[extraFilters]
	);

	const skipNextApply = useRef(false);

	useEffect(() => {
		if (defaultFilters) {
			if (Array.isArray(defaultFilters.filter) && defaultFilters.filter.length > 0) {
				setExtraFilters((prevFilters) =>
					prevFilters.map((filterItem) => {
						const saved = defaultFilters.filter.find((f) => {
							return f.key[0] === filterItem.filterValues.key[0];
						});
						return saved ? { ...filterItem, filterValues: saved } : filterItem;
					})
				);
			}
			skipNextApply.current = true;
		}
	}, [defaultFilters]);

	useEffect(() => {
		if (onApplyFilters) {
			if (skipNextApply.current) {
				skipNextApply.current = false;
				return;
			}
			onApplyFilters(body);
		}
	}, [body]);

	const { ref, data, loading } = useFetchAndParseOnView({
		url: REPORT.DELIVERY_COMPANY.GET_DELIVERY_AGING_LIST_CLIENT,
		body,
		parseData: (data) => prepareChartData({ data: data?.deliveryCounts, t })
	});

	return (
		<Card ref={ref}>
			<ChartHeader
				title={widgetName || 'deliveryAging'}
				filter={
					<>
						<Button
							id="filters-button"
							lordIcon="https://storage.googleapis.com/tryoto-public/lordicon-compressed/outline/filter-icon.json.gz"
							ref={setFilterMenuButtonRef}
							variant="borderless"
							label="filters"
							className="py-0"
						/>
						<CompositeFilter
							filtersMenuButtonRef={filtersMenuButtonRef}
							filtersList={extraFilters}
							setFiltersList={setExtraFilters}
						/>
					</>
				}
			/>
			<BarChartV2 data={data} loading={loading} />
		</Card>
	);
};

export default OverviewCharts;
